import { keepPreviousData as keepData } from "@tanstack/react-query";
import { useAuthQuery } from "@bn/api";
import { getPerformanceByDepots } from "@api/v1/methods";
import { CashFlowAndPerformanceStatisticsList } from "@api/v1/model";
import { ACCOUNT_PERFORMANCE_BY_ASSET_TYPES } from "@api/cacheKeys";
import { sameDateInUTC } from "@helpers";

export interface Attrs {
  accountIds?: number[];
  startDate?: Date;
  endDate?: Date;
  interval?: "day" | "week" | "month" | "year";
  keepPreviousData?: boolean;
}

export const usePerformanceByAssetClass = (attrs: Attrs) => {
  const {
    accountIds,
    startDate,
    endDate,
    interval,
    keepPreviousData = true,
  } = attrs;

  const query = useAuthQuery<
    CashFlowAndPerformanceStatisticsList,
    AxiosApiError,
    CashFlowAndPerformanceStatisticsList
  >(
    [ACCOUNT_PERFORMANCE_BY_ASSET_TYPES, attrs],
    ({ token }) =>
      getPerformanceByDepots(token, {
        accountIds: accountIds,
        startDate: startDate
          ? sameDateInUTC(startDate).toISOString()
          : undefined,
        endDate: endDate ? sameDateInUTC(endDate).toISOString() : undefined,
        interval: interval,
        cashFlowAndPerformanceStatisticsParamsList: [
          { assetClasses: [], includeCash: false, includeDividends: true },
          {
            assetClasses: ["11_stock", "71_massets", "91_managed"],
            includeCash: false,
            includeDividends: false,
          },
          {
            assetClasses: ["21_fund"],
            includeCash: false,
            includeDividends: false,
          },
          {
            assetClasses: ["22_etf"],
            includeCash: false,
            includeDividends: false,
          },
          {
            assetClasses: ["51_certos"],
            includeCash: false,
            includeDividends: false,
          },
          {
            assetClasses: ["31_bond"],
            includeCash: false,
            includeDividends: false,
          },
        ],
      }),
    {
      enabled: !!accountIds && accountIds.length > 0,
      placeholderData: keepPreviousData ? keepData : undefined,
    }
  );

  const [total, allHoldings, funds, etfs, derivative, bonds] = query.data
    ?.cashFlowAndPerformanceStatisticsList
    ? query.data?.cashFlowAndPerformanceStatisticsList
    : [];

  return { ...query, total, allHoldings, funds, etfs, derivative, bonds };
};
